import { useLanguage } from "@/LanguageContext";
import { motion } from "framer-motion";
import { Stethoscope, TreePine, UtensilsCrossed, ShoppingCart, Users } from "lucide-react";

const NATURE_IMG =
  "https://customer-assets-gfyr7b9c.emergentagent.net/job_senior-care-valais/artifacts/25c554039fa24adf_841864622_1105309251856160_8691174048717973970_n.jpg";
const SOCIAL_IMG =
  "https://static.prod-images.emergentagent.com/jobs/89794d8d-5285-4401-827b-391b661dda7a/images/d602906f8bb16706a752da8d778baf99f77edb73e2548d49b8d0aeb54fbe6fc8.png";
const MEDICAL_IMG =
  "https://images.unsplash.com/photo-1758691462477-976f771224d8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwxfHxlbGRlcmx5JTIwbWVkaWNhbCUyMGFwcG9pbnRtZW50JTIwZG9jdG9yfGVufDB8fHx8MTc3NjM2MDU4NHww&ixlib=rb-4.1.0&q=85";
const MEAL_IMG =
  "https://images.unsplash.com/photo-1683541606184-fecb068b26eb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwxfHxlbGRlcmx5JTIwbWVhbCUyMHNoYXJpbmclMjB0YWJsZSUyMHdhcm18ZW58MHx8fHwxNzc2MzYwNTkwfDA&ixlib=rb-4.1.0&q=85";
const SHOPPING_IMG =
  "https://images.unsplash.com/photo-1614863910598-a61532198778?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwxfHxlbGRlcmx5JTIwZ3JvY2VyeSUyMHNob3BwaW5nJTIwaGVscGVyJTIwY2FyZWdpdmVyfGVufDB8fHx8MTc3NjM2MDU5NXww&ixlib=rb-4.1.0&q=85";

const icons = [Stethoscope, TreePine, UtensilsCrossed, ShoppingCart, Users];
const images = [MEDICAL_IMG, NATURE_IMG, MEAL_IMG, SHOPPING_IMG, SOCIAL_IMG];

export default function ServicesSection() {
  const { t, language } = useLanguage();
  const items = t("services.items");

  const promenadeAlt = {
    fr: "Une accompagnante et une dame souriante lors d'une sortie avec vue sur les Alpes",
    de: "Eine Begleiterin und eine lächelnde Dame bei einem Ausflug mit Blick auf die Alpen",
    en: "A companion and a smiling lady on an outing with a view of the Alps",
  };

  return (
    <section
      id="services"
      data-testid="services-section"
      className="py-24 lg:py-32 bg-[#E8E0D4]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <p className="text-sm font-body uppercase tracking-[0.2em] text-[#6FA8B6] mb-4">
            {t("services.label")}
          </p>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#2C3E50]">
            {t("services.title")}
          </h2>
        </motion.div>

        <div className="flex flex-wrap justify-center gap-6">
          {Array.isArray(items) &&
            items.map((item, i) => {
              const Icon = icons[i];
              const img = images[i];
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                  className="bg-[#F7F3EE] rounded-3xl border border-[#D4CABC]/50 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300 overflow-hidden w-full sm:w-[calc(50%-12px)] lg:w-[calc(33.333%-16px)]"
                  data-testid={`service-card-${i}`}
                >
                  {img && (
                    <div className="h-48 overflow-hidden">
                      <img
                        src={img}
                        alt={i === 1 ? (promenadeAlt[language] || promenadeAlt.fr) : item.title}
                        loading={i === 0 ? undefined : "lazy"}
                        className="w-full h-full object-cover"
                        style={i === 1 ? { objectPosition: "center 30%" } : undefined}
                      />
                    </div>
                  )}
                  <div className="p-8">
                    <div className="w-12 h-12 bg-[#6FA8B6]/10 rounded-2xl flex items-center justify-center mb-5">
                      <Icon size={22} className="text-[#6FA8B6]" strokeWidth={1.5} />
                    </div>
                    <h3 className="font-heading text-xl text-[#2C3E50] mb-3">
                      {item.title}
                    </h3>
                    <p className="font-body text-sm text-[#2C3E50]/60 leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                </motion.div>
              );
            })}
        </div>
      </div>
    </section>
  );
}
